/**
 * Schema.org JSON-LD helpers (EcoSense Schema Markup Integration Guide v1.0).
 *
 * All helpers return plain objects — stringify them inside <Helmet>:
 *   <Helmet>
 *     <script type="application/ld+json">{JSON.stringify(getFAQSchema(faqs, lang))}</script>
 *   </Helmet>
 *
 * Rule to keep in mind everywhere: Google only accepts schema values that are
 * also visible on the page, so every helper is fed from the same API data the
 * component renders.
 */

import { topMenu } from './data/topMenu';
import {
  footerMenu,
  footerPrivacyPolicy,
  footerQuickLinks,
  footerServices,
} from './data/footer/data';
import { data as comprehensiveStudies } from './comprehensive-studies-data';

export const SITE_URL = 'https://www.ecosense.am';
export const ORGANIZATION_ID = `${SITE_URL}/#organization`;
export const WEBSITE_ID = `${SITE_URL}/#website`;

// URL segment "am" is the Armenian locale, but the ISO / hreflang code is "hy".
export const LANG_TO_HREFLANG = {
  am: 'hy',
  en: 'en',
  ru: 'ru',
};

const DEFAULT_PHONE = '+37444500400';

// TODO: replace with per-branch hours once the API exposes them — several
// branches have different Saturday / Sunday hours (see guide, §4.3).
const DEFAULT_OPENING_HOURS = [
  {
    '@type': 'OpeningHoursSpecification',
    dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
    opens: '08:00',
    closes: '18:00',
  },
  {
    '@type': 'OpeningHoursSpecification',
    dayOfWeek: 'Saturday',
    opens: '08:00',
    closes: '13:00',
  },
];

/**
 * Picks a value out of a multilingual API field ({ am, en, ru }) and falls back
 * to English. Accepts plain strings too, so callers do not have to guard.
 */
export const pickLang = (field, lang = 'en') => {
  if (!field) return '';
  if (typeof field === 'string') return field;
  return field[lang] || field.en || '';
};

/** Strips HTML, entities and collapses whitespace — schema values must be plain text. */
export const toPlainText = (value) => {
  if (!value) return '';
  return String(value)
    .replace(/<\/?[^>]+(>|$)/g, ' ')
    .replace(/&nbsp;|&#160;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/\s+/g, ' ')
    .trim();
};

/** Localized field -> plain text, in one step. */
const plain = (field, lang) => toPlainText(pickLang(field, lang));

/**
 * Keeps `description` fields short: the guide asks for 1–2 sentences.
 * Handles the Armenian full stop (։) alongside latin punctuation.
 */
export const toShortDescription = (value, sentences = 2) => {
  const text = toPlainText(value);
  if (!text) return '';
  const parts = text.match(/[^.։!?]+[.։!?]+/g);
  if (!parts || parts.length <= sentences) return text;
  return parts.slice(0, sentences).join(' ').replace(/\s+/g, ' ').trim();
};

/** Drops empty / undefined keys so the output never ships null values. */
const compact = (object) => Object.entries(object).reduce((acc, [key, value]) => {
  if (value === undefined || value === null || value === '') return acc;
  if (Array.isArray(value) && value.length === 0) return acc;
  return { ...acc, [key]: value };
}, {});

/*
 * Schema 1 (MedicalOrganization + Organization) and Schema 2 (WebSite) are not
 * built here: they carry no page data, so they are inlined as static
 * <script type="application/ld+json"> blocks in public/index.html. Rendering
 * them through react-helmet put them in the DOM only after React mounted, which
 * left them out of the HTML source that crawlers and validators read.
 *
 * The ids below are still the ones those blocks declare, so the schemas built
 * here can point at them (branchOf, performer, publisher).
 */

/**
 * Schema 3 — LocalBusiness / MedicalOrganization, one per branch.
 *
 * `branch` is an item of the `address` array returned by the addresses API
 * (id, address, latitude, longitude, phone, mobile, email, is_main).
 *
 * The @id is language-neutral on purpose: a branch is one entity, so the same
 * id must be reused across /am, /en and /ru, otherwise Google sees three
 * separate businesses at the same coordinates.
 */
export const getBranchSchema = (branch, lang = 'en') => {
  if (!branch) return null;
  const streetAddress = plain(branch.address, lang);

  return compact({
    '@context': 'https://schema.org',
    '@type': ['LocalBusiness', 'MedicalOrganization'],
    '@id': `${SITE_URL}/#branch-${branch.id}`,
    name: streetAddress ? `EcoSense — ${streetAddress}` : 'EcoSense',
    branchOf: { '@id': ORGANIZATION_ID },
    telephone: plain(branch.phone, lang) || plain(branch.mobile, lang) || DEFAULT_PHONE,
    email: branch.email || undefined,
    address: compact({
      '@type': 'PostalAddress',
      streetAddress,
      addressLocality: 'Yerevan',
      addressCountry: 'AM',
    }),
    geo: branch.latitude && branch.longitude
      ? {
        '@type': 'GeoCoordinates',
        latitude: Number(branch.latitude),
        longitude: Number(branch.longitude),
      }
      : undefined,
    openingHoursSpecification: branch.opening_hours || DEFAULT_OPENING_HOURS,
    url: `${SITE_URL}/${lang}/addresses?address=${branch.id}`,
  });
};

/**
 * Schema 4 — MedicalTest, for /:lang/section-description/:slug.
 *
 * `test` is the payload of the service API: title, description, method,
 * biomass, prerequisites, slug — all multilingual and HTML-formatted, so every
 * field goes through the plain-text conversion first.
 */
export const getTestSchema = (test, lang = 'en') => {
  if (!test || Object.keys(test).length === 0) return null;

  const name = plain(test.title, lang);
  const slug = pickLang(test.slug, lang);
  const alternateName = ['am', 'ru', 'en']
    .filter((code) => code !== lang)
    .map((code) => plain(test.title, code))
    .filter((value) => value && value !== name);

  return compact({
    '@context': 'https://schema.org',
    '@type': 'MedicalTest',
    '@id': slug ? `${SITE_URL}/${lang}/section-description/${slug}` : undefined,
    name,
    alternateName,
    description: toShortDescription(pickLang(test.description, lang)),
    bodyLocation: plain(test.biomass, lang) || undefined,
    preparation: plain(test.prerequisites, lang) || undefined,
    howPerformed: plain(test.method, lang) || undefined,
    performer: { '@id': ORGANIZATION_ID },
  });
};

/**
 * Schema 5 — FAQPage.
 *
 * `faqs` is the array rendered by the accordion ({ question, answer }), so the
 * markup always matches the visible text. Google shows at most ~10 items, and
 * answers must be plain text.
 */
export const getFAQSchema = (faqs, lang = 'en') => {
  const items = (faqs || [])
    .map((faq) => ({
      question: plain(faq.question, lang),
      answer: plain(faq.answer, lang),
    }))
    .filter((faq) => faq.question && faq.answer)
    .slice(0, 10);

  if (items.length === 0) return null;

  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: items.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };
};

/**
 * Schema 6 — BreadcrumbList, for inner pages.
 *
 * `items` is [{ name, url }] in visible order; `name` must match the visible
 * breadcrumb link text and `url` must be the full canonical URL of that level.
 */
export const getBreadcrumbSchema = (items) => {
  const trail = (items || []).filter((item) => item && item.name && item.url);
  if (trail.length === 0) return null;

  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: trail.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: toPlainText(item.name),
      item: item.url,
    })),
  };
};

/**
 * Localized labels for the breadcrumb trail.
 *
 * The names are pulled straight out of the header / footer menu definitions, so
 * a breadcrumb entry always carries the same text as the visible link to that
 * page (guide, §8.2). Menu URLs are identical in all three languages, so the
 * English one is used as the key.
 */
const HOME_LABEL = { am: 'Գլխավոր', en: 'Home', ru: 'Главная' };

const ROUTE_LABELS = [
  ...topMenu,
  ...footerQuickLinks,
  ...footerServices,
  ...footerMenu,
  ...footerPrivacyPolicy,
].reduce((acc, item) => {
  const path = item.url?.en;
  if (!path || acc[path]) return acc;
  return { ...acc, [path]: item.title };
}, {
  '/comprehensive-studies': {
    am: comprehensiveStudies.title_am,
    en: comprehensiveStudies.title_en,
    ru: comprehensiveStudies.title_ru,
  },
});

/**
 * { name, url } for a known top-level route, so a page can prepend its section
 * (e.g. Laboratory above a single test) to the trail.
 */
export const getRouteBreadcrumb = (path, lang = 'en') => {
  const label = ROUTE_LABELS[path];
  if (!label) return null;
  return { name: pickLang(label, lang), url: `${SITE_URL}/${lang}${path}` };
};

/**
 * Builds the [{ name, url }] trail for the current route, in visible order.
 *
 * `extra` appends levels the route alone cannot describe — the parent section
 * and the test title on /section-description/:slug, the doctor name on
 * /doctor/:id. Returns an empty array for the homepage and for routes with no
 * known label (account, checkout, thank-you), so those pages emit no
 * BreadcrumbList at all instead of a trail that only says "Home".
 */
export const getBreadcrumbTrail = (pathname, lang = 'en', extra = []) => {
  const segments = String(pathname || '').split('/').filter(Boolean);
  // First segment is the language prefix (/am, /en, /ru).
  const [, ...rest] = segments;
  if (rest.length === 0) return [];

  const trail = [{
    name: HOME_LABEL[lang] || HOME_LABEL.en,
    url: `${SITE_URL}/${lang}`,
  }];

  const label = ROUTE_LABELS[`/${rest[0]}`];
  if (label) {
    trail.push({
      name: pickLang(label, lang),
      url: `${SITE_URL}/${lang}/${rest[0]}`,
    });
  }

  (extra || []).forEach((item) => {
    if (item && item.name && item.url) trail.push(item);
  });

  return trail.length > 1 ? trail : [];
};
